import Vue from 'vue';
import Component from 'vue-class-component';
import { Emit, Inject, Prop, Ref } from 'vue-property-decorator';
import domtoimage from 'dom-to-image';
import { TOAST_TYPES } from '@/plugins/ed-toast/values/ed-toast.values';
import { Section } from '@/courses/models/section.model';
import ToolsService from '@/providers/tools';
import { DesignerElementTypes } from '@/components/designer/designer.enum';
import { ScreenProvider } from '@/shared/providers/screen.provider';
import { COURSES_EVENTS } from '@/courses/values/courses-events.values';
import EdDeckCardChangeNameModal from '@/courses/components/flash-cards/ed-deck-card-change-name-modal/ed-deck-card-change-name-modal.component.vue';

@Component({
  name: 'ed-deck-editor',
  components: {
    EdDesigner: () => import(/* EdDesigner */'@/components/designer/components/ed-designer/ed-designer.component.vue'),
    EdDeckCardChangeNameModal
  }
})
export default class EdDeckEditorComponent extends Vue {
  @Prop() readonly contentId: number;
  @Prop() readonly deckId: number;
  @Prop({ default: null }) readonly deck: Section;

  @Inject()
  ScreenProvider: ScreenProvider;

  card: Section = null;
  frontCardContent: any = { elements: [] };
  frontCardHTML = '';
  backCardContent: any = { elements: [] };
  backCardHTML = '';
  sideSelected = 'front-card';
  isSaving = false;
  cardName = '';
  canvasContainerSizes = {
    width: 700,
    height: 434,
  };
  maxCanvasContainerSizes = {
    width: 700,
    height: 434
  };
  scale = 1;
  aspectRatio = 1.61;
  isCardSizesCalculated = false;

  @Ref('designer') readonly designer!: any;
  @Ref('frontHTML') readonly frontHTML!: any;
  @Ref('backHTML') readonly backHTML!: any;

  @Emit('changeCardSelected')
  emitChangeCardSelected(card: Section): Section {
    return card;
  }

  get isFrontSelected() {
    return this.sideSelected === 'front-card';
  }

  get isBackSelected() {
    return this.sideSelected === 'back-card';
  }

  get hasFrontInfo() {
    return this.getIfCardSideHasContent(this.frontCardContent);
  }

  get hasBackInfo() {
    return this.getIfCardSideHasContent(this.backCardContent);
  }

  get frontSideClasses() {
    return this.getSideClasses(this.isFrontSelected, this.hasFrontInfo);
  }

  get backSideClasses() {
    return this.getSideClasses(this.isBackSelected, this.hasBackInfo);
  }

  get isExtraSmallWidthContainer() {
    return this.canvasContainerSizes.width < 330;
  }

  get isSmallWidthContainer() {
    return this.canvasContainerSizes.width < 370;
  }

  get hasTheEditionBackSide() {
    return this.card?.sections?.length > 1;
  }

  get canSave(): boolean {
    const isNotSaving = !this.isSaving;

    switch (this.cardType) {
      case 'SINGLE':
        return this.hasFrontInfo && isNotSaving;
      case 'DOUBLE':
        return this.hasBackInfo && this.hasFrontInfo && isNotSaving;
      default: // Both
        return this.hasFrontInfo && isNotSaving;
    }
  }

  get isEdition(): boolean {
    return this.card?.id ? true : false;
  }

  get cardType(): string {
    return this.deck?.text;
  }

  get isSingleCard(): boolean {
    return this.cardType === 'SINGLE';
  }

  get canvasContainerStyles() {
    return {
      width: `${this.canvasContainerSizes.width}px`,
      height: `${this.canvasContainerSizes.height}px`,
    };
  }

  get hasCardsDown() {
    if (!this.isEdition) {
      return false;
    }

    const index = this.deck.sections.findIndex(c => c.id === this.card?.id);
    const nextCard = this.deck.sections[index + 1];

    return nextCard ? true : false;
  }

  get hasCardsUp() {
    if (!this.isEdition) {
      return false;
    }

    const index = this.deck.sections.findIndex(c => c.id === this.card?.id);
    const nextCard = this.deck.sections[index - 1];
    
    return nextCard ? true : false;
  }

  created() {
    this.ScreenProvider.hideScrollOnBody();
  }

  beforeDestroy() {
    this.ScreenProvider.setScrollOnBody();
  }

  getIfCardSideHasContent(sideContent) {
    if (sideContent?.elements?.length === 0) return false;

    const hasTextElementWithContent = sideContent?.elements?.some(
      e => e.type === DesignerElementTypes.Text && e.meta?.content.length > 0
    );
    const hasOtherElements = sideContent?.elements?.some(
      e => e.type !== DesignerElementTypes.Text
    );

    return hasTextElementWithContent || hasOtherElements;
  }

  setCanvasContainerSizes() {
    const { width, height } = this.designer.$el.getBoundingClientRect();

    let widthAvailable = this.maxCanvasContainerSizes.width;

    if (width < (this.maxCanvasContainerSizes.width + 100)) {
      widthAvailable = width - 100;
    }

    this.canvasContainerSizes.width = widthAvailable;
    this.canvasContainerSizes.height = widthAvailable / this.aspectRatio;
    this.scale = widthAvailable / this.maxCanvasContainerSizes.width;
    this.isCardSizesCalculated = true;
  }

  getSideClasses(isSideSelected: boolean, hasSideInfo: boolean) {
    return {
      selected: isSideSelected,
      disabled: this.isSaving,
      filled: hasSideInfo,
    };
  }

  async loadCard(card: Section, tab = 0) {
    this.designer.loadDesign({ elements: [] });
    let cardStructure = null;

    await ToolsService.sleep(100);
    this.setCanvasContainerSizes();
    
    this.card = card;
    this.cardName = card?.name || 'Card';

    if (card) {
      if (card.sections[0].type !== 'CARD_DESIGNER') {
        cardStructure = await this.getCardFromLegacyStructure(card, tab);
      } else {
        cardStructure = await this.getCardFromDesignerStructure(card);
      }

      this.setCardSizes(cardStructure);
    } else {
      this.openCardNameModal();
      this.frontCardContent = { elements: [] };
      this.backCardContent = { elements: [] };
    }
   
    this.loadCardContent();
  }

  setCardSizes(cardStructure: any) {
    const { frontCardContent, backCardContent } = cardStructure;

    this.frontCardContent = {
      ...frontCardContent,
      elements: this.getElementsToLoadWithScale(frontCardContent.elements),
    };

    if (cardStructure.backCardContent) {
      this.backCardContent = {
        ...backCardContent,
        elements: this.getElementsToLoadWithScale(backCardContent.elements),
      };
    }
  }

  async getCardFromDesignerStructure(card: Section) {
    let backCardContent = null;
    let frontCardContent = await this.getElementsFromDesignerCardSide(card.sections[0]);

    if (this.hasTheEditionBackSide) {
      backCardContent = await this.getElementsFromDesignerCardSide(card.sections[1]);
    }

    return {
      frontCardContent,
      backCardContent,
    }
  }

  async getElementsFromDesignerCardSide(sideCard: Section) {
    const sideCardContent = JSON.parse(sideCard.text);
    const sideCardSourceHash = sideCardContent.source;
    const sideCardSourceAssets = sideCard.assetsInfo.find(a => a.hash == sideCardSourceHash);
    const sideCardSource = await fetch(sideCardSourceAssets.url).then(response => response.text());

    return JSON.parse(sideCardSource);
  }

  async getCardFromLegacyStructure(card: Section, tab = 0) {
    let backCardContent = null;
    let frontCardContent = {
      elements: await this.getElementsFromLegacyCardSide(card.sections[0]),
    };

    if (this.hasTheEditionBackSide) {
      backCardContent = {
        elements: await this.getElementsFromLegacyCardSide(card.sections[1]),
      };
    }

    return {
      frontCardContent,
      backCardContent,
    }
  }

  async getElementsFromLegacyCardSide(sideCard: Section): Promise<any[]> {
    const elements = [];
    const sideCardContent = JSON.parse(sideCard.text);
    const sideCardImageHash = sideCardContent.background || sideCardContent.image;
    const sideCardAssetInfo = sideCard.assetsInfo.find(a => a.hash == sideCardImageHash);

    if (sideCardAssetInfo) {
      elements.push({
        type: DesignerElementTypes.Image,
        position: { x: 0, y: 0, z: 1, },
        sizes: { width: this.canvasContainerSizes.width, height: this.canvasContainerSizes.height, },
        meta: { src: sideCardAssetInfo.url, },
      });
    }

    const sideCardTextHash = sideCardContent.html;
    const sideCardTextInfo = sideCard.assetsInfo.find(a => a.hash == sideCardTextHash);

    if (sideCardTextInfo?.url) {
      const sideCardTextContent = await fetch(sideCardTextInfo.url).then(response => response.text());

      elements.push({
        type: DesignerElementTypes.Text,
        position: { x: 0, y: 0, z: 2, },
        sizes: { width: this.canvasContainerSizes.width, height: this.canvasContainerSizes.height, },
        meta: { content: sideCardTextContent, },
      });
    }

    return elements;
  }

  async selectSide(value: string) {
    this.sideSelected = value;
    
    await ToolsService.sleep(100);
    this.loadCardContent();
    await ToolsService.sleep(100);
    this.designer.clearSelection();
  }

  loadCardContent() {
    const { elements } = this.isFrontSelected ? this.frontCardContent : this.backCardContent;

    this.designer.loadDesign({ elements });
  }

  getElementsToLoadWithScale(elements: any[], scale = this.scale): any[] {
    return elements.map(e => {
      return {
        ...e,
        position: {
          x: e.position.x * scale,
          y: e.position.y * scale,
          z: e.position.z,
        },
        sizes: {
          width: e.type == 'text' ? e.sizes.width : e.sizes.width * scale,
          height: e.type == 'text' ? e.sizes.height : e.sizes.height * scale,
        }, 
        scale: this.scale
      }
    });
  }

  getElementsWithScale(elements: any[], scale = this.scale): any[] {
    return elements.map(e => {
      return {
        ...e,
        scale: scale
      }
    });
  }

  saveCardContent(content) {
    if (this.isFrontSelected) {
      this.frontCardContent = {'elements': this.getElementsWithScale(content.elements)};
    } else {
      this.backCardContent = {'elements': this.getElementsWithScale(content.elements)};
    }
  }

  async setCardSideFinalDesignImage(side: string, payload: any) {
    this.selectSide(`${side}-card`);
    await ToolsService.sleep(500);

    payload[`${side}File`] = await domtoimage.toBlob(this[`${side}HTML`]);
    payload[`${side}File`] = await domtoimage.toBlob(this[`${side}HTML`]);
    payload[`${side}File`] = await domtoimage.toBlob(this[`${side}HTML`]);
  }

  async getPayloadToSave() {
    let firstSide = 'front';
    let secondSide = null;

    const payload: any = {};

    if (!this.isSingleCard) {
      firstSide = this.sideSelected === 'front-card' ? 'front' : 'back';
      secondSide = this.sideSelected === 'front-card' ? 'back' : 'front';
    }
    
    await this.setCardSideFinalDesignImage(firstSide, payload);

    if (secondSide && this.hasBackInfo) {
      await this.setCardSideFinalDesignImage(secondSide, payload);
      
      this.selectSide(`${firstSide}-card`);

      payload.backSource = this.getSideCardPayload(this.backCardContent);
    }

    payload.frontSource = this.getSideCardPayload(this.frontCardContent);
    payload.name = this.cardName;

    return payload;
  }

  getSideCardPayload(side) {
    if (this.scale === 1) {
      return side;
    }

    return {
      ...side,
      elements: this.getElementsToLoadWithScale(side.elements, 1 / this.scale),
    }
  }

  async saveCard() {
    if (!this.canSave || this.isSaving) {
      return;
    }

    this.designer.clearSelection();
    ToolsService.sleep(100);
    
    if (this.isEdition) {
      this.updateCard();
    } else {
      this.saveNewCard();
    }
  }

  async saveNewCard() {
    try {
      this.isSaving = true;

      const payload: any = await this.getPayloadToSave();
      const id = await this.$Courses.Store.createCardA( {
        contentId: this.contentId,
        deckSectionId: this.deckId,
        ...payload,
      });

      this.card = null;
      this.card = this.deck.sections.find(el => el.id === id);
      this.emitChangeCardSelected(this.card);
      
      setTimeout(() => {
        this.$EdToast.create({ 
          msg:  `${this.$t('courses.deck.info.cardSaved')}`,
          type: TOAST_TYPES.SUCCESS,
          top: '3rem',
          timeout: 3000
        });
        this.isSaving = false;
      }, 200);
    } catch(err) {
      this.isSaving = false;
      this.$EdToast.create({ 
        msg:  `${this.$t('courses.errors.errorSavingObject')}`,
        type: TOAST_TYPES.DANGER,
        top: '3rem',
        timeout: 3000
      });
    }
  }

  async updateCard() {
    try {
      this.isSaving = true;
      
      const payload: any = await this.getPayloadToSave();
      const id = await this.$Courses.Store.updateCardA( {
        contentId: this.contentId,
        deckSectionId: this.deckId,
        cardSectionId: this.card?.id,
        ...payload
      });
      this.card = null;
      this.card = this.deck.sections.find(el => el.id === id);
      this.$EdToast.create({ 
        msg:  `${this.$t('courses.deck.info.cardSaved')}`,
        type: TOAST_TYPES.SUCCESS,
        top: '3rem',
        timeout: 3000
      });
    } catch(err) {
      this.$EdToast.create({ 
        msg:  `${this.$t('courses.errors.errorSavingObject')}`,
        type: TOAST_TYPES.DANGER,
        top: '3rem',
        timeout: 3000
      });
    } finally {
      this.isSaving = false;
    }
  }

  openCardNameModal() {
    this.$EdModal.createModal(EdDeckCardChangeNameModal, {
      cardName: this.cardName,
      onSave: this.updateCardName
    })
  }

  updateCardName(name: string) {
    this.cardName = name;

    this.$EdModal.closeModal();
  }

  closeCardNameModal() {
    this.$EdModal.closeModal();
  }

  goToPrevCard() {
    this.$edEventBus.$emit(COURSES_EVENTS.DECK_MOVE_UP);
  }

  goToNextCard() {
    this.$edEventBus.$emit(COURSES_EVENTS.DECK_MOVE_DOWN);
  }
}